import React from 'react';
import { Radio, Button, Input } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { F924, F9241 } from '../../../services/pw9224Api';
import { dateToInput, inputToDate, getApprovalStatusText, getApprovalFlagText } from '../helpers';
import { Mode } from '../hooks/usePW9224Mode';

const { TextArea } = Input;

interface TripFormProps {
  formData: Partial<F924>;
  details: Partial<F9241>[];
  names: Record<string, string>;
  mode: Mode;
  isEditing: boolean;
  selectedRow: number;
  setField: (key: keyof F924, value: F924[keyof F924]) => void;
  setDetailField: (idx: number, key: keyof F9241, value: F9241[keyof F9241]) => void;
  setSelectedRow: (idx: number) => void;
  resolveSingleName: (nameKey: string, type: string, code: string) => void;
  addDetailRow: () => void;
  deleteDetailRow: (idx: number) => void;
}

const TripForm: React.FC<TripFormProps> = React.memo(({
  formData, details, names, mode, isEditing, selectedRow,
  setField, setDetailField, setSelectedRow, resolveSingleName,
  addDetailRow, deleteDetailRow,
}) => {
  const isOverseas = formData.f924_17 === 2;
  const hasApproval = !!(formData.f924_80 && formData.f924_80.trim());

  return (
    <div className="trip-form-content">
      {/* Header fields */}
      <div className="form-grid header-grid">
        <label>出差單號</label>
        <input
          value={formData.f924_01 || ''}
          readOnly
          className="readonly-field"
          style={{ maxWidth: 120 }}
        />

        <label>出差人員代號</label>
        <div className="lookup-field">
          <input
            value={formData.f924_11 || ''}
            disabled={!isEditing || mode === 'edit'}
            onChange={e => setField('f924_11', e.target.value)}
            onBlur={() => resolveSingleName('employee', 'employee', formData.f924_11 || '')}
            style={{ maxWidth: 100 }}
          />
          <span className="lookup-name">{names.employee || ''}</span>
        </div>

        <label>部門</label>
        <input value={formData.f924_31 || ''} readOnly className="readonly-field" style={{ maxWidth: 80 }} />

        <label>申請日期</label>
        <input
          type="date"
          value={dateToInput(formData.f924_12)}
          disabled={!isEditing}
          onChange={e => setField('f924_12', inputToDate(e.target.value) as unknown as F924[keyof F924])}
          style={{ maxWidth: 140 }}
        />
      </div>

      {/* Main container */}
      <div className="main-container">
        <div className="form-grid">
          <label>代理人</label>
          <div className="lookup-field">
            <select
              value={formData.f924_13 || ''}
              disabled={!isEditing}
              onChange={e => {
                setField('f924_13', e.target.value);
                resolveSingleName('agent', 'employee', e.target.value);
              }}
              style={{ maxWidth: 100 }}
              className="ant-select-like"
            >
              <option value="">-- 選擇 --</option>
            </select>
            <span className="lookup-name">{names.agent || ''}</span>
          </div>

          <label>事由</label>
          <input
            value={formData.f924_14 || ''}
            disabled={!isEditing}
            onChange={e => setField('f924_14', e.target.value)}
            maxLength={60}
          />

          <label>地點</label>
          <input
            value={formData.f924_15 || ''}
            disabled={!isEditing}
            onChange={e => setField('f924_15', e.target.value)}
            maxLength={60}
          />
        </div>

        <div className="date-time-row">
          <div className="date-group">
            <label>開始日</label>
            <input
              type="date"
              value={dateToInput(formData.f924_21)}
              disabled={!isEditing}
              onChange={e => setField('f924_21', inputToDate(e.target.value) as unknown as F924[keyof F924])}
            />
            <label>時間</label>
            <input
              value={formData.f924_22 || ''}
              disabled={!isEditing}
              onChange={e => setField('f924_22', e.target.value)}
              placeholder="HH:MM"
              maxLength={5}
              style={{ maxWidth: 70 }}
            />
          </div>
          <div className="date-group">
            <label>結束日</label>
            <input
              type="date"
              value={dateToInput(formData.f924_23)}
              disabled={!isEditing}
              onChange={e => setField('f924_23', inputToDate(e.target.value) as unknown as F924[keyof F924])}
            />
            <label>時間</label>
            <input
              value={formData.f924_24 || ''}
              disabled={!isEditing}
              onChange={e => setField('f924_24', e.target.value)}
              placeholder="HH:MM"
              maxLength={5}
              style={{ maxWidth: 70 }}
            />
          </div>
        </div>

        <div className="trip-type-row">
          <Radio.Group
            value={formData.f924_17 || 1}
            disabled={!isEditing}
            onChange={e => setField('f924_17', e.target.value)}
          >
            <Radio value={1}>國內</Radio>
            <Radio value={2}>國外</Radio>
          </Radio.Group>
        </div>

        {/* Detail grid - overseas details */}
        <div className="detail-section">
          <label className="section-hint">以下明細, 國外出差才需輸入....</label>
          <table className="detail-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>項次</th>
                <th style={{ width: 100 }}>日期</th>
                <th style={{ width: 75 }}>開始時間</th>
                <th style={{ width: 75 }}>結束時間</th>
                <th style={{ width: 160 }}>國外地點</th>
                <th style={{ width: 130 }}>對象</th>
                <th style={{ width: 140 }}>預定任務</th>
              </tr>
            </thead>
            <tbody>
              {details.map((d, idx) => (
                <tr
                  key={idx}
                  className={idx === selectedRow ? 'selected-row' : ''}
                  onClick={() => setSelectedRow(idx)}
                >
                  <td>{d.f9241_02}</td>
                  <td>
                    <input
                      type="date"
                      value={dateToInput(d.f9241_11)}
                      disabled={!isEditing || !isOverseas}
                      onChange={e => setDetailField(idx, 'f9241_11', inputToDate(e.target.value) as unknown as F9241[keyof F9241])}
                    />
                  </td>
                  <td>
                    <input
                      value={d.f9241_12 || ''}
                      disabled={!isEditing || !isOverseas}
                      onChange={e => setDetailField(idx, 'f9241_12', e.target.value)}
                      placeholder="HH:MM"
                      maxLength={5}
                    />
                  </td>
                  <td>
                    <input
                      value={d.f9241_13 || ''}
                      disabled={!isEditing || !isOverseas}
                      onChange={e => setDetailField(idx, 'f9241_13', e.target.value)}
                      placeholder="HH:MM"
                      maxLength={5}
                    />
                  </td>
                  <td>
                    <input
                      value={d.f9241_14 || ''}
                      disabled={!isEditing || !isOverseas}
                      onChange={e => setDetailField(idx, 'f9241_14', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      value={d.f9241_15 || ''}
                      disabled={!isEditing || !isOverseas}
                      onChange={e => setDetailField(idx, 'f9241_15', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      value={d.f9241_16 || ''}
                      disabled={!isEditing || !isOverseas}
                      onChange={e => setDetailField(idx, 'f9241_16', e.target.value)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="detail-actions">
            <span>項數: {formData.f924_30 || details.length}</span>
            <Button
              icon={<PlusOutlined />}
              disabled={!isEditing || !isOverseas}
              onClick={addDetailRow}
              size="small"
            >
              明細新增
            </Button>
            <Button
              icon={<DeleteOutlined />}
              disabled={!isEditing || !isOverseas}
              onClick={() => deleteDetailRow(selectedRow)}
              size="small"
              danger
            >
              明細刪除
            </Button>
          </div>
        </div>

        {/* Approval status indicators */}
        <div className="approval-status-row">
          <span className="approval-flag">{getApprovalFlagText(formData.f924_79 || '')}</span>
          <span className="approval-status">{getApprovalStatusText(formData.f924_80 || '')}</span>
        </div>

        {/* Approval chain */}
        <div className="approval-section">
          <div className="approval-row">
            <label>初核:</label>
            <div className="lookup-field">
              <input value={formData.f924_81 || ''} readOnly className="readonly-field" style={{ maxWidth: 100 }} />
              <span className="lookup-name">{names.initApprover || ''}</span>
            </div>
            <label>核准日</label>
            <input
              type="date"
              value={dateToInput(formData.f924_82)}
              readOnly
              className="readonly-field"
            />
          </div>

          <div className="approval-row">
            <label>審核:</label>
            <div className="lookup-field">
              <input value={formData.f924_72 || ''} readOnly className="readonly-field" style={{ maxWidth: 100 }} />
              <span className="lookup-name">{names.reviewer || ''}</span>
            </div>
            <label>核准日</label>
            <input
              type="date"
              value={dateToInput(formData.f924_73)}
              readOnly
              className="readonly-field"
            />
          </div>

          <div className="approval-row">
            <label>複核:</label>
            <div className="lookup-field">
              <input value={formData.f924_84 || ''} readOnly className="readonly-field" style={{ maxWidth: 100 }} />
              <span className="lookup-name">{names.reApprover || ''}</span>
            </div>
            <label>核准日</label>
            <input
              type="date"
              value={dateToInput(formData.f924_85)}
              readOnly
              className="readonly-field"
            />
          </div>
        </div>

        {/* Remark + Entry info */}
        <div className="bottom-row">
          <div className="remark-section">
            <label>備註</label>
            <TextArea
              value={formData.f924_16 || ''}
              disabled={!isEditing}
              onChange={e => setField('f924_16', e.target.value)}
              rows={3}
            />
          </div>
          <div className="entry-info">
            <label>入單人員</label>
            <div className="lookup-field">
              <input value={formData.f924_91 || ''} readOnly className="readonly-field" style={{ maxWidth: 100 }} />
              <span className="lookup-name">{names.entryPerson || ''}</span>
            </div>
            <label>入單日</label>
            <input
              type="date"
              value={dateToInput(formData.f924_92)}
              readOnly
              className="readonly-field"
            />
          </div>
        </div>
      </div>
    </div>
  );
});

TripForm.displayName = 'TripForm';
export default TripForm;
